import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isNil } from 'lodash'
import { IconTrash } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { Loader } from '@/components/loaders/Loader'
import { Toggle } from '@/components/forms/Checkbox'
import ConfirmModal from '@/components/layout/modals/ConfirmModal'
import {
  fetchCallDataDeletion,
  saveCallDataDeletion,
} from '@/reducers/voiceAgents/voiceAgents.actions'

// =============================================================================
// CallDataDeletionPanel — arms deletion of Cartesia's own copy of this agent's
// call data (transcript, audio, logs) once our copy is stored. Balto-admin
// only, both here and on the backend, which also audits every change.
//
// Enabling is irreversible per call and affects future calls only, so it goes
// through a confirmation; disabling applies straight away. The toggle always
// renders the *stored* value — never an optimistic one — so a failed save can
// never look like it worked.
// =============================================================================

const CallDataDeletionPanel = ({ voiceAgentId }) => {
  const dispatch = useDispatch()
  const stored = useSelector((state) => state.voiceAgents.callDataDeletionById[voiceAgentId])
  const isLoading = useSelector((state) => Boolean(state.voiceAgents.loading.callDataDeletion))
  const storedError = useSelector((state) => state.voiceAgents.error.callDataDeletion)
  const isSaving = useSelector((state) => Boolean(state.voiceAgents.loading.saveCallDataDeletion))

  const [confirmOpen, setConfirmOpen] = useState(false)

  useEffect(() => {
    dispatch(fetchCallDataDeletion(voiceAgentId))
  }, [dispatch, voiceAgentId])

  // Both the flag and the failure are only ever trusted for the agent this
  // panel is mounted for. The value is keyed by agent, so a read still in
  // flight for the previously-viewed agent resolves into its own key; the
  // single error slot is shared, so it's matched on the id it carries.
  const isLoaded = !isNil(stored)
  const enabled = Boolean(stored)
  const loadError = storedError?.voiceAgentId === voiceAgentId ? storedError : null

  const applyEnabled = async (nextEnabled) => {
    const saved = await dispatch(saveCallDataDeletion(voiceAgentId, nextEnabled))
    if (saved) {
      setConfirmOpen(false)
    }
  }

  const handleToggle = (event, { checked }) => {
    if (checked) {
      setConfirmOpen(true)
      return
    }
    applyEnabled(false)
  }

  // A 403/404 means this agent has no such control to offer — an expected-empty
  // navigation, not an actionable error.
  if (loadError?.kind === 'unavailable') {
    return null
  }

  if (loadError) {
    return (
      <div className="voice-agents__side-card" data-testid="call-deletion-panel">
        <ErrorMessage content="Couldn't load call-data deletion settings." />
        <Button small onClick={() => dispatch(fetchCallDataDeletion(voiceAgentId))}>
          Try again
        </Button>
      </div>
    )
  }

  if (!isLoaded || isLoading) {
    return <Loader centered label="Loading call-data deletion settings…" />
  }

  return (
    <div
      className="voice-agents__override-editor voice-agents__side-card"
      data-testid="call-deletion-panel"
    >
      <div className="voice-agents__override-editor-head">
        <div className="voice-agents__override-editor-title">
          <span className="voice-agents__override-editor-avatar">
            <IconTrash size={14} />
          </span>
          <span className="voice-agents__override-editor-label">Cartesia call data</span>
        </div>
      </div>

      <Toggle
        label="Delete call data at Cartesia"
        checked={enabled}
        disabled={isSaving}
        onChange={handleToggle}
        data-testid="call-deletion-toggle"
      />
      <p className="voice-agents__call-deletion-hint">
        Cartesia&apos;s transcript, audio and logs for a call are deleted once our own copy is
        stored and post-call processing has finished. Applies to future calls only.
      </p>

      <ConfirmModal
        show={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Enable call-data deletion?"
        confirmLabel="Enable deletion"
        busyLabel="Enabling…"
        busy={isSaving}
        destructive
        onConfirm={() => applyEnabled(true)}
        testIdPrefix="call-deletion"
      >
        <p>
          Cartesia&apos;s copy of every call this agent handles from now on — transcript, audio and
          logs — is <strong>permanently deleted</strong> once our own copy is stored. Deleted data
          can&apos;t be recovered from Cartesia. Calls already handled aren&apos;t affected.
        </p>
      </ConfirmModal>
    </div>
  )
}

export default CallDataDeletionPanel
